// One media item, large: a video that plays with its own controls, a photo at full size, what
// it is and where it came from, and the post it was made for when there is one.
import type { MediaDto } from '@str/api/types';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { errorMessage } from '@/lib/api';
import { longDate } from '@/lib/format';
import { mediaStyle } from '@/lib/scenes';

function fmtDuration(s: number) {
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** Where an item came from, in plain words: a photo's provenance is part of inspecting it. */
function sourceLine(m: Pick<MediaDto, 'source' | 'createdAt' | 'kind'>): string {
  const when = longDate(m.createdAt);
  switch (m.source) {
    case 'listing':
      return `Imported from your listing · ${when}`;
    case 'uploaded':
      return `Uploaded by you · ${when}`;
    case 'reference':
      return `Added from a link · ${when}`;
    case 'generated':
      return `Made here · ${when}`;
    default:
      return when;
  }
}

/** The item's name, editable: a linked photo's default name is only a stand-in (J18). */
function RenameForm({
  label,
  onRename,
}: {
  label: string;
  onRename: (label: string) => Promise<void>;
}) {
  const [name, setName] = useState(label);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const next = name.trim();
  const save = async () => {
    if (!next || next === label) return;
    setSaving(true);
    try {
      await onRename(next);
      setName(next);
      setError(null);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <form
      className="media-preview-rename"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <input
        className="input"
        aria-label="Name"
        maxLength={80}
        value={name}
        onChange={(e) => {
          setName(e.target.value);
          setError(null);
        }}
        aria-invalid={error ? true : undefined}
      />
      <button
        type="submit"
        className="btn btn-outline hv"
        disabled={saving || !next || next === label}
      >
        Save name
      </button>
      {error && (
        <span className="err" role="alert">
          {error}
        </span>
      )}
    </form>
  );
}

export function MediaPreview({
  item,
  onClose,
  onRename,
}: {
  item: MediaDto | null;
  onClose: () => void;
  /** Saves a new name; rejects with the reason when it could not. */
  onRename?: (label: string) => Promise<void>;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  // The tile that opened it gets focus back when it closes, however it closes.
  const opener = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (item && !d.open) {
      opener.current = document.activeElement as HTMLElement | null;
      d.showModal();
    }
    if (!item && d.open) d.close();
  }, [item]);
  useEffect(() => {
    if (item) return;
    const back = opener.current;
    opener.current = null;
    if (back && back.isConnected) back.focus();
  }, [item]);
  if (!item) return null;
  const kind = item.kind === 'video' ? 'Video' : item.kind === 'audio' ? 'Audio' : 'Photo';
  return (
    <dialog
      ref={ref}
      className="media-preview"
      aria-label={`${item.label || kind} · ${kind}`}
      onClose={onClose}
      onClick={(e) => e.target === e.currentTarget && onClose()}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          onClose();
        }
      }}
    >
      <div className="media-preview-body">
        <button type="button" className="btn btn-outline hv" autoFocus onClick={onClose}>
          Close
        </button>
        {item.kind === 'video' && item.url ? (
          <video src={item.url} controls playsInline preload="metadata" aria-label={item.label} />
        ) : item.kind === 'audio' && item.url ? (
          <audio src={item.url} controls preload="metadata" aria-label={item.label} />
        ) : item.url ? (
          <img src={item.url} alt={item.label || kind} />
        ) : (
          <div
            className="media-preview-scene"
            style={mediaStyle(item)}
            role="img"
            aria-label={item.label}
          />
        )}
        <div className="media-preview-meta">
          <strong>{item.label || kind}</strong>
          <span>
            {kind}
            {item.durationSeconds != null ? ` · ${fmtDuration(item.durationSeconds)}` : ''}
            {item.mimeType ? ` · ${item.mimeType}` : ''}
          </span>
          <span>{sourceLine(item)}</span>
          {onRename && <RenameForm key={item.id} label={item.label} onRename={onRename} />}
          {item.contentMediaError && (
            <span className="err">Cannot be used: {item.contentMediaError}</span>
          )}
          {item.postId && (
            <Link to={`/app/create?post=${item.postId}`} className="media-preview-post">
              Open the post it was made for ▸
            </Link>
          )}
        </div>
      </div>
    </dialog>
  );
}
